import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Button, Card, CardContent, Grid, Icon } from '@mui/material'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Select from 'components/forms/Select'
import Textarea from 'components/forms/Textarea'
import { shallowEqual, useSelector } from 'react-redux'
import Input from 'components/forms/Input'
import SimpleSelectionTable, { ActiveParameterFilterField, ParameterFilterField } from 'components/datatable/SimpleSelectionTable'
import { intersectionWith, isUndefined, keyBy, keys } from 'lodash'
import { normalizeString } from 'utils/StringUtil'
import { formatParameterSelectedPopup } from 'campaign/utils/CampaignUtils'
import { SANDRE, nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getLocalizationLabel, getLocalizationPicto } from 'utils/AnalyseUtils'
import Autocomplete from 'components/forms/Autocomplete'
import useSandreList from 'utils/customHook/useSandreList'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import { getHour } from 'utils/DateUtil'
import { onChangeHour } from 'utils/FormUtils'
import Checkbox from 'components/forms/Checkbox'
import useBoolean from 'utils/customHook/useBoolean'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { whiteColor, skyBlue } from 'utils/constants/ColorTheme'
import { filterActiveElements } from 'utils/ListUtil'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { getQualificationSelectOptions, getStatusSelectOptions } from 'utils/StatusUtil'

const ANALYSIS_FIELDS_STORAGE = 'OPERATION_ANALYSIS_FIELDS'

const OperationStep = ({
    operation = {},
    setOperation = () => { },
    points = [],
}) => {
    const {
        supports,
        contributors,
    } = useSelector(store => ({
        supports: store.SupportReducer.supports,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const activeSupports = filterActiveElements(supports)

    const formattedPoints = useMemo(() => {
        return points.map(p => {
            const support = activeSupports.find(s => s.code === `${p.codeSupport}`)
            return {
                name: `${p.name || (support?.name ?? 'Eau')} ${showSandreCode ? `[${p.identifiant || (support?.code ?? '031')}]` : ''} ${p.code ?? ''} ${p.startDepth || p.endDepth ? `${p.startDepth ?? '(...)'}m -> ${p.endDepth ?? '(...)'}m` : ''}`,
                id: p.idPoint,
                code: support?.code,
            }
        })
    }, [points, activeSupports, showSandreCode])

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={2} rowSpacing={1}>
                    <Grid size={3}>
                        <SimpleDatePicker
                            value={operation.date}
                            label={i18n.sampleDate}
                            onChange={date => setOperation(p => ({ ...p, date }))}
                            obligatory
                        />
                    </Grid>
                    <Grid size={3}>
                        <Input
                            title={i18n.sampleHour}
                            value={getHour(operation.date)}
                            onChange={v => onChangeHour(v, v2 => setOperation(p => ({ ...p, date: v2 })), {}, operation.date)}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            label={i18n.support}
                            options={activeSupports}
                            value={operation.support}
                            onChange={support => setOperation(p => ({ ...p, support }))}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            label={i18n.samplePoint}
                            options={formattedPoints}
                            value={operation.point}
                            onChange={point => setOperation(p => ({ ...p, point }))}
                            obligatory
                            sortFunc={({ id }) => id}
                            keyValue={'id'}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.location}
                            value={operation.localization}
                            onChange={localization => setOperation(p => ({ ...p, localization }))}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={contributors}
                            label={i18n.producer}
                            value={operation.producer}
                            nullLabel='&nbsp;'
                            keyLabel='labelDisplay'
                            onChange={producer => setOperation(p => ({ ...p, producer }))}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={contributors}
                            label={i18n.sampler}
                            value={operation.sampler}
                            nullLabel='&nbsp;'
                            keyLabel='labelDisplay'
                            onChange={sampler => setOperation(p => ({ ...p, sampler }))}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={contributors}
                            label={i18n.laboratory}
                            value={operation.labo}
                            nullLabel='&nbsp;'
                            keyLabel='labelDisplay'
                            onChange={labo => setOperation(p => ({ ...p, labo }))}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Textarea
                            title={i18n.comment}
                            value={operation.comment}
                            onChange={comment => setOperation(p => ({ ...p, comment }))}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

OperationStep.propTypes = {
    operation: PropTypes.shape({
        dateStart: PropTypes.number,
        hourStart: PropTypes.number,
        support: PropTypes.number,
        producer: PropTypes.number,
        sampler: PropTypes.number,
        labo: PropTypes.number,
        comment: PropTypes.string,
    }),
    setOperation: PropTypes.func,
    points: PropTypes.arrayOf(PropTypes.shape({})),
}

const ParameterStep = ({
    title = i18n.pesticidesSelection,
    analysis = [],
    setAnalysis = () => { },
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const activeParameters = useMemo(() => formatParameterSelectedPopup(parameters.filter(p => p.active), showSandreCode), [parameters, showSandreCode])

    const selectedParameters = analysis.map(a => a.parameter)

    const onChange = listParameter => {
        setAnalysis(prevAnalysis => {
            const indexedAnalysis = keyBy(prevAnalysis, 'parameter')
            return listParameter.map(p => indexedAnalysis[p] ?? { parameter: p })
        })
    }

    return (
        <Grid container>
            <Grid size={12}>
                <SimpleSelectionTable
                    onChange={onChange}
                    title={title}

                    listData={activeParameters}
                    selectedList={selectedParameters}

                    listHeaders={['name', 'type', 'nature', 'status']}
                    listTitle={i18n.nonSelectedParameters}
                    selectedListTitle={i18n.selectedParameters}
                    maxHeightTable={'40vh'}

                    filterField={ActiveParameterFilterField}
                    filterFunction={(list, { selection = '-1', listParam, searchValue, shouldDisplayNonActive = false }) => {
                        const filterActive = shouldDisplayNonActive ? list : list.filter(p => p.active)
                        const filterSelection = selection !== '-1' ? intersectionWith(filterActive, listParam, (elem, id) => elem.id === id) : filterActive
                        const searchValueFormat = normalizeString(searchValue)
                        return searchValue ? filterSelection.filter(p => p.labelSearch.includes(searchValueFormat)) : filterSelection
                    }}
                />
            </Grid>
        </Grid>
    )
}

ParameterStep.propTypes = {
    title: PropTypes.string,
    analysis: PropTypes.arrayOf(PropTypes.shape({
        parameter: PropTypes.string,
    })),
    setAnalysis: PropTypes.func,
}

const AnalysisFields = ({
    analysisFields = {},
    setAnalysisFields = () => { },
    onValidate = () => { },
}) => {
    const {
        units,
        fractions,
        remarks,
    } = useSelector(store => ({
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        remarks: store.OperationReducer.remarks,
    }), shallowEqual)

    const analysisPlace = useSandreList('ANALYSES.LIEU')
    const analysisAccreditation = useSandreList(SANDRE.ANALYSES_ACCREDITATION)
    const analysisDifficulty = useSandreList(SANDRE.ANALYSES_DIFFICULTE)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const { value: shouldDisplayNonActive, toggle: toggleDisplayNonActive } = useBoolean(false)

    const statusList = useMemo(() => getStatusSelectOptions(), [])
    const qualificationList = useMemo(() => getQualificationSelectOptions(), [])

    const activeUnits = useMemo(() => shouldDisplayNonActive ? units : units.filter(s => s.active === '1'), [shouldDisplayNonActive, units])
    const activeFractions = useMemo(() => shouldDisplayNonActive ? fractions : fractions.filter(s => s.active === '1'), [fractions, shouldDisplayNonActive])

    useEffect(() => {
        if (!activeFractions.length || isUndefined(analysisFields.fraction) || activeFractions.some(f => f.id === analysisFields.fraction)) return
        setAnalysisFields(prevFields => ({ ...prevFields, fraction: undefined }))
    }, [activeFractions])

    useEffect(() => {
        if (!activeUnits.length || isUndefined(analysisFields.unit) || activeUnits.some(u => u.id === analysisFields.unit)) return
        setAnalysisFields(prevFields => ({ ...prevFields, unit: undefined }))
    }, [activeUnits])

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                    <Grid size={3}>
                        <Autocomplete
                            label={i18n.unit}
                            options={activeUnits}
                            value={analysisFields.unit}
                            onChange={unit => setAnalysisFields(prevFields => ({ ...prevFields, unit }))}
                            keyLabel='symbolWithName'
                            keyFilter={['name', 'symbol', 'code']}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.fraction}
                            options={activeFractions}
                            value={analysisFields.fraction}
                            keyValue='id'
                            onChange={fraction => setAnalysisFields(prevFields => ({ ...prevFields, fraction }))}
                            clearFunction
                            displayWithCode={showSandreCode}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.place}
                            options={analysisPlace}
                            value={analysisFields.localization}
                            onChange={localization => setAnalysisFields(prevFields => ({ ...prevFields, localization: !isUndefined(localization) ? `${localization}` : undefined }))}
                            clearFunction
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Checkbox
                            label={i18n.displayNonActiveReferentials}
                            checked={shouldDisplayNonActive}
                            onChange={toggleDisplayNonActive}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={remarks}
                            value={analysisFields.remark}
                            label={i18n.remark}
                            keyLabel={showSandreCode ? 'nameWithCode' : 'name'}
                            keyValue='code'
                            returnNull
                            noSort
                            onChange={remark => setAnalysisFields(prevFields => ({ ...prevFields, remark }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={analysisAccreditation}
                            value={analysisFields.accreditation}
                            label={i18n.accreditation}
                            returnNull
                            onChange={accreditation => setAnalysisFields(prevFields => ({ ...prevFields, accreditation }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={analysisDifficulty}
                            value={analysisFields.difficulty}
                            label={i18n.difficulty}
                            returnNull
                            onChange={difficulty => setAnalysisFields(prevFields => ({ ...prevFields, difficulty }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={statusList}
                            value={analysisFields.status}
                            label={i18n.status}
                            nullLabel='&nbsp;'
                            onChange={status => setAnalysisFields(prevFields => ({ ...prevFields, status: !isUndefined(status) ? `${status}` : undefined }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={qualificationList}
                            value={analysisFields.qualification}
                            label={i18n.qualification}
                            nullLabel='&nbsp;'
                            onChange={qualification => setAnalysisFields(prevFields => ({ ...prevFields, qualification: !isUndefined(qualification) ? `${qualification}` : undefined }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Button
                            onClick={onValidate}
                            variant='contained'
                            color='primary'
                            fullWidth
                        >
                            {i18n.validate}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

AnalysisFields.propTypes = {
    analysisFields: PropTypes.shape({
        unit: PropTypes.string,
        fraction: PropTypes.string,
        localization: PropTypes.string,
        remark: PropTypes.string,
        accreditation: PropTypes.number,
        difficulty: PropTypes.number,
        status: PropTypes.string,
        qualification: PropTypes.string,
    }),
    setAnalysisFields: PropTypes.func,
    nbSelectedAnalysis: PropTypes.number,
    onValidate: PropTypes.func,
}

const AnalysisStep = ({
    analysis = [],
    setAnalysis = () => { },
}) => {
    const {
        parameters,
        units,
        fractions,
        remarks,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        remarks: store.OperationReducer.remarks,
    }), shallowEqual)

    const analysisAccreditation = useSandreList(SANDRE.ANALYSES_ACCREDITATION)
    const analysisDifficulty = useSandreList(SANDRE.ANALYSES_DIFFICULTE)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')

    const [selectedAnalysis, setSelectedAnalysis] = useState({}) // key: parameter code, value: boolean
    const [filter, setFilter] = useState({})
    const [analysisFields, setAnalysisFields] = useLocalStorage(ANALYSIS_FIELDS_STORAGE, {})

    const isSomeAnalysisSelected = keys(selectedAnalysis).some(key => selectedAnalysis[key])

    const formattedAnalysis = useMemo(() => {
        return analysis.map(a => {
            const isSelected = selectedAnalysis[a.parameter]
            const parameter = indexedParameters[a.parameter]
            const unit = indexedUnits[a.unit]
            const fraction = indexedFractions[a.fraction]

            const completeRemark = remarks.find(r => r.code === a.remark)
            const remark = showSandreCode ? completeRemark?.nameWithCode : completeRemark?.name
            const accreditation = analysisAccreditation.find(accre => accre.code === a.accreditation)?.name
            const difficulty = analysisDifficulty.find(diff => diff.code === a.difficulty)?.name

            return {
                parameter: showSandreCode ? parameter?.displayLabel : parameter?.displayName,
                unit: showSandreCode ? unit?.symbolWithCode : unit?.symbol,
                fraction: showSandreCode ? fraction?.labelWithCode : fraction?.name,
                place: !isUndefined(a.localization) && getLocalizationPicto(a.localization),
                check: (<Icon>{isSelected ? 'check_box' : 'check_box_outline_blank'}</Icon>),
                colorLine: isSelected ? skyBlue : whiteColor,
                code: a.parameter,
                remark,
                accreditation,
                difficulty,
                searchValue: [parameter?.name, parameter?.shortLabel, parameter?.code, parameter?.internalCode, unit?.symbolWithCode, fraction?.labelWithCode, remark, accreditation, difficulty, getLocalizationLabel(a.localization)].map((label = '') => normalizeString(label)),
            }
        })
    }, [analysis, analysisAccreditation, analysisDifficulty, indexedFractions, indexedParameters, indexedUnits, remarks, selectedAnalysis, showSandreCode])


    const filteredAnalysis = useMemo(() => {
        const { selection = '-1', listParam, searchValue } = filter
        const filterSelection = selection !== '-1' ? intersectionWith(formattedAnalysis, listParam, (a, code) => a.code === code) : formattedAnalysis
        const searchValueFormat = normalizeString(searchValue)
        return searchValue ? filterSelection.filter(p => p.searchValue.some(v => v.includes(searchValueFormat))) : filterSelection
    }, [filter, formattedAnalysis])

    const isAllAnalysisSelected = filteredAnalysis.every(a => selectedAnalysis[a.code])

    const onClick = () => {
        setSelectedAnalysis(prevSelected => {
            return filteredAnalysis.reduce((acc, a) => {
                acc[a.code] = !isAllAnalysisSelected
                return acc
            }, { ...prevSelected })
        })
    }

    const actions = filteredAnalysis.length ? [{
        icon: isAllAnalysisSelected ? 'check_box' : 'check_box_outline_blank',
        tooltip: i18n.edit,
        onClick,
    }] : []

    return (
        <Grid container justifyContent='center' rowSpacing={1}>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <ParameterFilterField
                            filter={filter}
                            setFilter={setFilter}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={i18n.analysis}
                    actions={actions}

                    rows={filteredAnalysis}
                    headers={['parameter', 'unit', 'fraction', 'remark', 'accreditation', 'difficulty', 'place', { key: 'check', value: '', sticky: ALIGN.RIGHT }]}

                    maxHeight={isSomeAnalysisSelected ? '36vh' : '45vh'}
                    onClickRow={({ code }) => setSelectedAnalysis(prevSelected => ({ ...prevSelected, [code]: !prevSelected[code] }))}
                    rowsPerPageOptions={nbPerPageLabel}
                />
            </Grid>
            {
                isSomeAnalysisSelected && (
                    <Grid size={12}>
                        <AnalysisFields
                            analysisFields={analysisFields}
                            setAnalysisFields={setAnalysisFields}
                            onValidate={() => {
                                setAnalysis(prevAnalysis => {
                                    return prevAnalysis.map(a => {
                                        if (selectedAnalysis[a.parameter]) {
                                            return { parameter: a.parameter, ...analysisFields }
                                        }
                                        return a
                                    })
                                })
                                setSelectedAnalysis({})
                            }}
                        />
                    </Grid>
                )
            }
        </Grid>
    )
}

AnalysisStep.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({
        parameter: PropTypes.string,
    })),
    setAnalysis: PropTypes.func,
}

export {
    AnalysisStep,
    OperationStep,
    ParameterStep,
}